import { Box, Typography, Button, AppBar, CardMedia, Divider, IconButton } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  ApplicationProps,
  Document,
  PAYMENT_DETAILS,
  esignApplicationResponse,
} from '../../redux-store/types/api-types';
import {
  esignApplication,
  getApplicationDetailsWithRefId,
} from '../../redux-store/actions/onBoarding';
import { useHistory } from 'react-router';
import { RootStateType } from '../../redux-store/reducers';
import { Footer } from '../commonComponents';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ESIGN_STATUS } from '../../utils/constant';
import { getDocuments, isFormValidForSubmission } from '../../redux-store/actions/application';
import { showError } from '../../redux-store/actions/auth';
import { ApplicationDetailsCommonLayout } from '../ApplicationDetails/index';
import {
  getNationalityList,
  getPaymentStatus,
  createLumpsumPayment,
  getAvailableBanks,
  updatePaymentType,
} from '../../redux-store/actions';
import { nationaliyType } from '../../redux-store/types/mdms';
import { PaymentPopup } from './PaymentMode';
import { PaymentSuccessPopup } from './PaymentSuccessPopup';
import { OfflinePaymentEsignPopup } from './OfflinePaymentEsignPopup';
import { PaymentFailurePopup } from './PaymentFailurePopup';
import {
  ESIGN_MESSAGE,
  isOverseasBank,
  isOverseasBankApplication,
  isPaymentStepComplete,
  scrollToBottom,
} from '../../utils/utilityFunctions';
import Loader from '../../utils/Loader';

export const Item = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  paddingTop: '10px',
  marginLeft: '2px',
  color: theme.palette.text.secondary,
  fontSize: '15px',
  fontWeight: 500,
  letterSpacing: '0.5px',
}));
export const ItemDetails = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  fontSize: '16px',
  marginLeft: '2px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
}));
export const Gridstyles = styled('div')(({ theme }) => ({
  paddingLeft: '10%',
  [theme.breakpoints.only('xs')]: {
    paddingLeft: 0,
  },
}));

export default function InvestorApplication(): JSX.Element {
  const { referenceId } = useParams<{ referenceId: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const { investor, auth } = useSelector((store: RootStateType) => store);
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState<ApplicationProps>();
  const applicationDetailRef = useRef<HTMLDivElement>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [showPaymentSuccessPopup, setShowPaymentSuccessPopup] = useState(false);
  const [showPaymentFailurePopup, setShowPaymentFailurePopup] = useState(false);
  const [showPaymentModePopup, setShowPaymentModePopup] = useState(false);
  const [showOfflinePaymentEsignPopup, setShowOfflinePaymentEsignPopup] = useState(false);
  const [isOnlinePaymentEnabled, setIsOnlinePaymentEnabled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async function () {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const actualStatus = urlParams.get('status')?.toLowerCase();
        const _application = (await dispatch(
          getApplicationDetailsWithRefId(referenceId, actualStatus)
        )) as unknown as ApplicationProps;
        const paymentStatus = _application?.payment?.status;
        const paymentType = _application?.paymentType;
        if (
          (paymentStatus &&
            ['pending', 'success', 'failure', 'expired'].includes(paymentStatus || '') &&
            paymentType === 'online') ||
          paymentType === 'offline'
        ) {
          setPaymentCompleted(true);
        }
        setApplication(_application);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    })();
    setLoading(true);

    (async function () {
      const urlParams = new URLSearchParams(window.location.search);
      const actualStatus = urlParams.get('status')?.toLowerCase();

      const paymentStatus = urlParams.get('paymentStatus');
      const orderId = urlParams.get('order_id');

      if (['pending', 'success'].includes(actualStatus || '')) {
        // Scroll to bottom when redirected back with paymentStatus
        scrollToBottom();
      }

      if (orderId) {
        try {
          const paymentStatusResponse = (await dispatch(getPaymentStatus(orderId))) as unknown as {
            status?: string;
          };
          const status = (paymentStatusResponse?.status || '').toLowerCase();
          if (['pending', 'success'].includes(status || '')) {
            setShowPaymentSuccessPopup(true);
            setPaymentCompleted(true);
          } else if (['failure', 'expired'].includes(status || '')) {
            setShowPaymentFailurePopup(true);
          }
        } catch (e) {
          typeof e === 'string' && dispatch(showError(e as string));
          setShowPaymentFailurePopup(true);
        } finally {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        return;
      }
      if (actualStatus) {
        if (['pending', 'success'].includes(actualStatus || '')) {
          setShowPaymentSuccessPopup(true);
          setPaymentCompleted(true);
          // window.history.replaceState({}, document.title, window.location.pathname);
        } else if (['failure', 'expired'].includes(actualStatus || '')) {
          setShowPaymentFailurePopup(true);
          // window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    })();
  }, [location]);

  useEffect(() => {
    const { token } = investor;
    if (!token) {
      history.push(`/investment-details/${referenceId}/details`);
    }
  }, []);

  useEffect(() => {
    document.title = 'MARCELLUS :: Application Details';
  }, []);

  const handleEsign = async () => {
    try {
      setIsSubmitting(true);
      const nationalitiesMdmsMasters = (await dispatch(
        getNationalityList()
      )) as unknown as nationaliyType;
      const documentsResponse = (await dispatch(
        getDocuments(application?.flowType || '', application?.fundFlowType || '')
      )) as unknown as Document;
      if (ESIGN_STATUS.PENDING_SIGNATURE !== application?.signDetails.status) {
        ESIGN_STATUS.NOT_GENERATED === application?.signDetails.status
          ? await isFormValidForSubmission(
              application as ApplicationProps,
              true,
              true,
              nationalitiesMdmsMasters,
              documentsResponse,
              auth.role
            )
          : await isFormValidForSubmission(
              application as ApplicationProps,
              true,
              false,
              nationalitiesMdmsMasters,
              documentsResponse,
              auth.role
            );
      }
      const { signDetails, flowType = '' } = application || {};
      if (signDetails?.url) {
        window.open(signDetails?.url, '_blank');
        return;
      }
      const response = (await dispatch(
        esignApplication(referenceId, flowType)
      )) as unknown as esignApplicationResponse;
      setApplication({ ...application, signDetails: response } as unknown as ApplicationProps);
      window.open(response?.url, '_blank');
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
      console.error((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMakePayment = async () => {
    try {
      setShowPaymentFailurePopup(false);
      if (!application?.id) {
        dispatch(showError('Application details are missing. Please refresh and try again.'));
        return;
      }
      if (application?.paymentUrl) {
        window.open(application?.paymentUrl, '_blank');
        return;
      }
      const selectedBank =
        application?.banks?.find((bank) => bank?.defaultBankAccount) || application?.banks?.[0];
      const isOverseas = isOverseasBank(selectedBank?.investorBankType);
      const bankCode = isOverseas ? selectedBank?.swiftCode || '' : selectedBank?.ifscCode || '';
      // const ifscCode = selectedBank?.ifscCode || '';

      if (!bankCode) {
        dispatch(
          showError(
            isOverseas
              ? 'Bank SWIFT code is missing. Please update bank details and try again.'
              : 'Bank IFSC code is missing. Please update bank details and try again'
          )
        );
        return;
      }

      setIsSubmitting(true);
      const availableBanksResponse = (await dispatch(
        getAvailableBanks({
          trtype: 'Lumpsum',
          paymode: 'Net Banking',
          ifsc: bankCode,
        })
      )) as unknown as { isNetbankingAvailable?: boolean };
      setIsOnlinePaymentEnabled(Boolean(availableBanksResponse?.isNetbankingAvailable));
      setShowPaymentModePopup(true);
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentModeConfirm = async (paymentType: string, paymentMode: string) => {
    try {
      if (!application?.id) {
        dispatch(showError('Application details are missing. Please refresh and try again.'));
        return;
      }
      await dispatch(
        updatePaymentType({ applicationId: application?.id || '', paymentType: paymentType })
      );
      if (paymentMode !== 'offline') {
        const callbackSuccessUrl = `${window.location.origin}${window.location.pathname}?paymentStatus=success`;
        const callbackFailureUrl = `${window.location.origin}${window.location.pathname}?paymentStatus=failed`;
        const paymentCreateResponse = (await dispatch(
          createLumpsumPayment({
            applicationId: application?.id,
            payment_mode: paymentMode,
            callback_success_url: callbackSuccessUrl,
            callback_failure_url: callbackFailureUrl,
          })
        )) as unknown as {
          url?: string;
          order_id?: string;
        };

        const paymentUrl = paymentCreateResponse?.url;

        if (paymentUrl) {
          window.open(paymentUrl, '_blank');
          return;
        }
        dispatch(showError('Unable to initiate payment. Please try again.'));
      } else if (paymentMode === 'offline') {
        setPaymentCompleted(true);
        setShowOfflinePaymentEsignPopup(true);
        // Defer scroll until after DOM updates
        scrollToBottom();
      }
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
    }
  };

  const handleContinueOffline = async () => {
    try {
      if (!application?.id) {
        dispatch(showError('Application details are missing. Please refresh and try again.'));
        return;
      }
      await dispatch(updatePaymentType({ applicationId: application.id, paymentType: 'offline' }));
      setShowPaymentFailurePopup(false);
      setShowOfflinePaymentEsignPopup(true);
      setPaymentCompleted(true);
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
    }
  };

  const handleOverseasProceedToEsign = async () => {
    try {
      if (!application?.id) {
        dispatch(showError('Application details are missing. Please refresh and try again.'));
        return;
      }
      await dispatch(updatePaymentType({ applicationId: application.id, paymentType: 'offline' }));
      setPaymentCompleted(true);
      await handleEsign();
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
    }
  };

  const paymentStepComplete = isPaymentStepComplete(paymentCompleted, application);
  const isOverseas = isOverseasBankApplication(application);
  const showEsignButton =
    paymentStepComplete &&
    application &&
    ![ESIGN_STATUS.SIGNED, ESIGN_STATUS.EXPIRED].includes(application.signDetails.status);

  return (
    <Box
      sx={{
        bgcolor: { xs: '', sm: 'rgba(238, 244, 251, 0.5)' },
      }}>
      <IconButton
        sx={{
          position: 'fixed',
          right: 0,
          bottom: 75,
          borderRadius: '5px  0 0 5px',
          '&,:hover': {
            bgcolor: 'primary.main',
          },
        }}
        aria-label="Back to Top"
        onClick={() => applicationDetailRef.current?.scrollIntoView({ behavior: 'smooth' })}>
        <ArrowUpwardIcon fontSize="large" sx={{ color: 'common.white' }} />
      </IconButton>
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: 'common.white' }}>
        <Box sx={{ padding: '20px' }}>
          <CardMedia component="img" src="/images/Logo.svg" alt="Logo" sx={{ width: '200px' }} />
        </Box>
      </AppBar>
      <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
      <Box sx={{ py: 5, px: { xs: 5, sm: 10 } }} ref={applicationDetailRef}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 2,
            justifyContent: 'space-between',
            mt: 10,
          }}>
          <Typography
            component="h1"
            sx={{ fontSize: 20, fontWeight: 600, color: 'primary.main', mb: 4 }}>
            Application Details
          </Typography>
          {application?.editable && (
            <IconButton disabled={isSubmitting}>
              <CardMedia
                component="img"
                src="/images/edit-icon-outlined.svg"
                alt="Edit Icon"
                sx={{ width: 'unset' }}
                onClick={() =>
                  history.push(`/investment-details/edit-application`, {
                    id: application?.id,
                    applicant1ReferenceId: application?.applicant1ReferenceId,
                  })
                }
              />
            </IconButton>
          )}
        </Box>
        {application && (
          <>
            <ApplicationDetailsCommonLayout
              loading={loading}
              application={application as ApplicationProps}
            />
            {!paymentStepComplete && (
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  sx={{
                    color: 'common.white',
                    minWidth: '200px',
                    mt: 4,
                    fontWeight: 600,
                    lineHeight: 1.5,
                  }}
                  onClick={handleMakePayment}
                  disabled={isSubmitting}>
                  Make Payment
                </Button>
              </Box>
            )}
            {showEsignButton && (
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  sx={{
                    color: 'common.white',
                    minWidth: '200px',
                    mt: 4,
                    fontWeight: 600,
                    lineHeight: 1.5,
                  }}
                  onClick={() =>
                    isOverseas && !paymentCompleted ? handleOverseasProceedToEsign() : handleEsign()
                  }
                  disabled={isSubmitting}>
                  {`e-Sign ${isSubmitting ? '...' : ''}`}
                </Button>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: 14,
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.7,
                    textAlign: 'center',
                  }}>
                  ({ESIGN_MESSAGE})
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
      <Footer />
      {showPaymentSuccessPopup && (
        <PaymentSuccessPopup
          onClose={() => setShowPaymentSuccessPopup(false)}
          onClickOk={async () => {
            setShowPaymentSuccessPopup(false);
            await handleEsign();
          }}
          okDisabled={isSubmitting}
        />
      )}
      {showPaymentFailurePopup && (
        <PaymentFailurePopup
          onClickOk={async () => {
            setShowPaymentFailurePopup(false);
            await handleEsign();
          }}
        />
      )}
      {showPaymentModePopup && (
        <PaymentPopup
          onClose={() => setShowPaymentModePopup(false)}
          onConfirm={handlePaymentModeConfirm}
          isOnlinePaymentEnabled={isOnlinePaymentEnabled}
        />
      )}
      {showOfflinePaymentEsignPopup && (
        <OfflinePaymentEsignPopup
          onClose={() => setShowOfflinePaymentEsignPopup(false)}
          onProceedWithEsign={async () => {
            setShowOfflinePaymentEsignPopup(false);
            await handleEsign();
          }}
          proceedDisabled={isSubmitting}
        />
      )}
      {isSubmitting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'white',
            zIndex: 9999, // Ensures it's above all other content
          }}>
          <Loader
            size={60}
            speed={2}
            text="Getting the forms ready for you to sign, please wait for a few moments."
          />
        </Box>
      )}
    </Box>
  );
}
